import { useMemo } from 'react';

import { getFoldersLOV } from 'api/folders/operations/getFoldersLOV';
import { folderPathsById } from 'helpers/folderPaths';

import { EditReference } from './EditReference';
import type { ReferenceSource } from './referenceSource';

interface Props {
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  // Restrict options to one zone's folders. Folders are zone-scoped, so the
  // picker mirrors the entity's selected zone; clears to all when undefined.
  readonly zoneName?: string | null;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
}

// Module-level so the references are stable — EditReference rebuilds its model when getKey/getLabel change identity.
const getKey = (path: string) => path;
const getLabel = (path: string) => path;

/**
 * Searchable folder dropdown keyed on the folder PATH string, for wire shapes that carry `folder_name` (a path) instead of
 * `folder_id` — the path-keyed counterpart to {@link EditFolderReference}. Eager-loaded (no `/folders` search endpoint; the
 * folder universe is small): loads the flat list once and filters client-side. Options are the folders' full paths scoped to
 * `zoneName`; the value is the plain path string, so callers need no wrapping.
 */
export const EditFolderPath = ({ value, onChange, zoneName, label = 'Folder', mandatory, disabled }: Props) => {
  // Rebuild the loader when the zone changes so the options re-scope. Same
  // (zoneName) yields the same function reference, so the model isn't churned.
  const source = useMemo<ReferenceSource<string>>(
    () => ({
      kind: 'eager',
      load: async () => {
        // Folders are zone-scoped: with no zone selected there are no valid
        // folders to offer.
        if (!zoneName) return [];
        const folders = await getFoldersLOV();
        const scoped = folders.filter((f) => f.zone_name === zoneName);
        const paths = folderPathsById(scoped);
        return scoped.map((f) => paths.get(f.folder_id) ?? f.name).sort((a, b) => a.localeCompare(b));
      },
    }),
    [zoneName]
  );

  return (
    <EditReference<string>
      label={label}
      value={value}
      onChange={onChange}
      source={source}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
    />
  );
};
